import { AlertTriangle } from 'lucide-react'
import z from 'zod'

import {
  type ClinicId,
  clinicIdSchema,
  pluralizeUnit,
  roundQuantity,
  type Stock,
  type WithNullable,
} from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { FormLayoutProvider } from '@/frontend/components/form/layouts/form-layout-context'
import { NumericInput } from '@/frontend/components/numeric-input'
import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import { useGetProduct } from '@/frontend/hooks/api/use-get-product'
import { usePatchStock } from '@/frontend/hooks/api/use-patch-stock'
import { usePostStock } from '@/frontend/hooks/api/use-post-stock'
import { formatPackBreakdown, hasBatchedStock } from '@/frontend/lib/stock'

/**
 * Schema enforces structure only - the warning for "usually batch-tracked"
 * products is a soft nudge rendered in the form, not a hard validation
 * failure. Server-side unique-index conflicts still surface as a 409.
 */
const schema = z.object({
  clinicId: z.enum(clinicIdSchema.options, { message: 'Please select a clinic' }),
  quantity: z
    .number({ message: 'Please provide a quantity' })
    .multipleOf(0.01, 'Maximum 2 decimal places'),
  batchNumber: z.string().nullish(),
  expiryDate: z.iso.date().nullish(),
})

// The schema is the strict, submittable contract; the form's live state loosens
// clinicId to null so the picker's "no selection" binds without a cast.
type StockRowFormValues = WithNullable<z.infer<typeof schema>, 'clinicId'>

type StockRowFormProps = {
  productId: string
  /** Pre-fill clinic when creating from a clinic-filtered view. */
  defaultClinicId?: ClinicId | null
  onSuccess?: () => void
  onCancel?: () => void
  existing?: Stock
}

/**
 * Unified form for creating and editing stock rows under a single product.
 *
 * Batch tracking is not configured per product anymore - instead we look at
 * existing stock rows across the practice and warn (non-blocking) when the
 * user is about to create an unbatched row for a product that has
 * historically been batch-tracked at any clinic.
 *
 * In edit mode the clinic field is locked - changing clinic would
 * effectively be a different stock row, and the unique index on
 * (clinic, product, batch) treats them as separate entities.
 *
 * Batch number / expiry date are hidden entirely when editing a row that
 * originated without a batch number - converting an unbatched row into a
 * batched row (or vice versa) isn't a meaningful operation; the right path
 * is a stocktake. For batched rows both fields stay editable; the server
 * surfaces a ConflictError if the new batch number collides with an
 * existing row.
 */
export function StockRowForm({
  productId,
  defaultClinicId,
  existing,
  onSuccess,
  onCancel,
}: StockRowFormProps) {
  const { mutateAsync: createStock } = usePostStock()
  const { mutateAsync: updateStock } = usePatchStock()
  // We refetch the product (with stock) inside the form so we can derive
  // "has this product been batched anywhere?" without requiring the caller to
  // thread stock state through.
  const { data: product } = useGetProduct(productId, { include: 'stock' })

  // Hide batch / expiry fields when editing an unbatched row - those
  // concepts don't apply, and we don't want to let users accidentally
  // "promote" the row.
  const showBatchFields = !existing || existing.batchNumber !== null

  const form = useAppForm({
    defaultValues: {
      clinicId: existing?.clinicId ?? defaultClinicId,
      // Round the stored level onto the 2dp grid the schema below enforces.
      // Stock quantities drift (1.77 stored as 1.7699999999999996), so seeding
      // raw would fail "Maximum 2 decimal places" for someone editing only the
      // batch number or expiry, on a field they never touched.
      quantity: existing ? roundQuantity(existing.quantity) : undefined,
      batchNumber: existing?.batchNumber,
      expiryDate: existing?.expiryDate,
    } as StockRowFormValues,
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value, formApi }) => {
      // The onSubmit validator already ran, so re-parsing recovers the strict
      // types it guaranteed (notably a non-null clinicId) without a manual guard.
      const parsed = schema.parse(value)
      try {
        if (existing) {
          await updateStock({
            id: existing.id,
            quantity: parsed.quantity,
            batchNumber: parsed.batchNumber,
            expiryDate: parsed.expiryDate,
          })
        } else {
          await createStock({ ...parsed, productId })
        }
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save stock row',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <FormLayoutProvider layout="stacked-fill">
          <div className="flex flex-col gap-4 py-2">
            <form.GlobalError />

            {/* Product context so staff can sanity-check they're editing the
                right row and see the pack layout at a glance. */}
            {product && (
              <div className="flex flex-col gap-0.5 border-b pb-3">
                <div className="font-medium">{product.name}</div>
                <div className="text-sm text-muted-foreground">
                  {product.sku} · {product.packLabel}
                </div>
              </div>
            )}

            <form.Field name="clinicId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Clinic"
                  required
                  render={({ id, disabled }) => (
                    <ClinicSearch
                      includeArchived={!!existing}
                      id={id}
                      value={field.state.value}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabled={disabled || !!existing}
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                  )}
                />
              )}
            </form.Field>

            {/* Custom render so the unit label and pack breakdown sit inline
                to the right of a compact numeric input, rather than the
                stacked-fill layout the rest of the form uses. */}
            <form.Field name="quantity">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Quantity"
                  required
                  render={({ id, disabled }) => {
                    const quantity = field.state.value ?? 0
                    const breakdown = product ? formatPackBreakdown(quantity, product) : null
                    return (
                      <div className="flex items-baseline gap-2">
                        <NumericInput
                          id={id}
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v ?? 0)}
                          onBlur={field.handleBlur}
                          disabled={disabled}
                          className="w-32"
                          placeholder="0"
                          aria-invalid={field.state.meta.errors.length > 0}
                        />
                        {product && (
                          <span className="text-muted-foreground">
                            {pluralizeUnit(product.unit, quantity)}
                            {breakdown && ` (${breakdown})`}
                          </span>
                        )}
                      </div>
                    )
                  }}
                />
              )}
            </form.Field>

            {showBatchFields && (
              <>
                <form.AppField name="batchNumber">
                  {(field) => <field.InputField label="Batch number" placeholder="LOT123456" />}
                </form.AppField>

                <form.AppField name="expiryDate">
                  {(field) => <field.DateInputField label="Expiry date" />}
                </form.AppField>
              </>
            )}

            {/* Non-blocking warning when the product has historically been
                batch-tracked across the Snoots but the user is leaving the
                batch/expiry fields blank. */}
            {showBatchFields && product && hasBatchedStock(product.stock) && (
              <form.Subscribe
                selector={(s) => ({
                  batchNumber: s.values.batchNumber,
                  expiryDate: s.values.expiryDate,
                })}
              >
                {({ batchNumber, expiryDate }) => {
                  const missing: string[] = []
                  if (!batchNumber) missing.push('a batch number')
                  if (!expiryDate) missing.push('an expiry date')
                  if (missing.length === 0) return null
                  return (
                    <Alert variant="warning">
                      <AlertTriangle />
                      <AlertDescription>
                        This product is usually batch-tracked at Snoots, but this row is missing{' '}
                        {missing.join(' and ')}. Continue only if you're sure.
                      </AlertDescription>
                    </Alert>
                  )
                }}
              </form.Subscribe>
            )}
          </div>
        </FormLayoutProvider>

        <div className="flex items-center justify-end gap-x-2 pt-3">
          {onCancel && (
            <Button size="lg" variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton>{existing ? 'Save Changes' : 'Add Stock Row'}</form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}
